import { useCallback, useEffect, useRef, useState } from 'react'
import {
    AppBar,
    AppBarSection,
    AppBarSpacer,
    Drawer,
} from '@progress/kendo-react-layout'
import UniversalCreateButton, {
    UNIVERSAL_CREATE,
} from 'components/DropdownSelectors/UniversalCreateButton'
import LanguageSelector from 'components/LanguageSelector'
import CreateClientModal from 'components/Modals/Client'
import LocationModal from 'components/Modals/Location'
import LoginModal from 'components/Modals/Login'
import ServiceModal from 'components/Modals/Service'
import SignupModal from 'components/Modals/Signup'
import StaffModal from 'components/Modals/Staff'
import TenantModal from 'components/Modals/Tenant'
import { ModalDataAttributes } from 'components/Modals/types'
import UpsellModal from 'components/Modals/Upsell'
import UserDropdown from 'components/Navbar/UserDropdown'
import { Wrapper } from 'components/types'
import UniversalSearch from 'components/UniversalSearch'
import { useAppContext } from 'context/AppContext'
import {
    Authenticated,
    NotAuthenticated,
    useAuth,
    tenant_logo_url,
} from 'context/AuthContext'
import Cookies from 'js-cookie'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { classNames } from 'utils/dom-helpers'
import { toTitleCase } from 'utils/string-helper'
import ClassModal from 'components/Modals/Class'
import UpsellPopup from 'components/Modals/Upsell/popup'
import MembershipModal from 'components/Modals/Membership'
import PaymentModal from 'components/Modals/Payment'
import CreditCardModal from 'components/Modals/CreditCard'
import GlobalDialog from 'components/Modals/GlobalDialog'
import GlobalSchedulingLimitsModal from 'components/Modals/GlobalSchedulingLimits'
import MembershipAddModal from 'components/Modals/Client/Memberships/MembershipAddModal'
import PurchaseSummaryModal from 'components/Modals/Client/Memberships/PurchaseSummary'
import CreateCouponModal from 'components/Modals/Coupons'
import CustomBookingPageModal from '../../components/Modals/CustomBookingPage'
import { getTranslation } from 'utils/language-helper'
import AppointmentDrawer from 'components/appointmentDrawer/AppointmentDrawer'
import POSContextContainer from 'context/posContext/POSContextContainer'
import POSModal from 'components/Modals/POS'
import AppointmentDrawerContainer from 'context/appointmentDrawerContext/AppointmentDrawerContainer'
import { useTenantContext } from 'context/TenantContext'
import TenantSelector from 'components/DropdownSelectors/TenantSelector'
import { Popup } from '@progress/kendo-react-popup'
import { RoleTypes } from 'types/staff'
import MembershipEditModal from '../../components/Modals/Client/Memberships/MembershipEditModal'
import { AppointmentAddModal } from '../../components/Modals/Client/Appointments/AddAppointmentModal'
import StaffConnectCalendarModal from 'components/Modals/Staff/Calendar'
import OrderEditModal from 'components/Modals/Client/Orders/OrderEditModal'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import LostConnection from 'components/LostConnection'
import ClientSidebar from 'components/ClientSidebar'
import Translation from 'components/Translation'
import CalendarPrintModal from 'components/Modals/Calendar/CalendarPrint'
import MobileSidebar from 'components/Sidebar/MobileSidebar'
import { NotificationIcon } from 'utils/custom-icons'
import getConfig from 'next/config'

const Sidebar = dynamic(() => import('./Sidebar'), { ssr: false })

function Dashboard({
    redirect = '/',
    children,
}: { redirect?: string } & Wrapper) {
    const router = useRouter()
    const {
        attributes,
        setAttributes,
        user,
        hasRole,
        user_profile,
        LoginModal: LoginModalContext,
        CreateClientModal: NewClientModal,
        StaffModal: NewStaffModal,
        ServiceModal: NewServiceModal,
        MembershipModal: NewMembershipModal,
        LocationModal: NewLocationModal,
        ClassModal: NewClassModal,
        POSModal: authPOSModal,
        CustomBookingPageModal: bookingPageModalCustom,
        is_drawer_expanded,
        toggleDrawerSize,
    } = useAuth()
    const { entitlements, is_free_tier, sidebar_items } = useTenantContext()
    const { setGlobalError } = useAppErrorApiContext()
    const { translations, locale, loadComponentTranslations, is_mobile } =
        useAppContext()
    const [show_location_popup, setShowLocationPopup] = useState(false)
    const [show_staff_popup, setShowStaffPopup] = useState(false)
    const add_new_anchor = useRef(null)
    const [display_appointmentDrawer, setDisplayAppointmentDrawer] =
        useState(false)
    const [popup_anim_css, animatePopup] = useState<string>('bg-opacity-0')
    const [show_mobile_search_bar, toggleMobileSearchBar] =
        useState<boolean>(false)
    const [show_mobile_sidebar, toggleShowMobileSidebar] =
        useState<boolean>(false)
    const {scheduling} = router.query;

    useEffect(() => {
        if (is_drawer_expanded && is_mobile) {
            toggleDrawerSize(false)
        }
    }, [is_mobile])

    useEffect(() => {
        if (user_profile?.user_type == 'CLIENT') {
            router.replace({
                pathname: '/book/client/appointments/',
                query: router.query,
            })
        } else if (user_profile?.roles?.length == 0) {
            setGlobalError({
                message: getTranslation('no_user_roles', translations),
            })
        }
    }, [user_profile])

    useEffect(() => {
        if (display_appointmentDrawer) animatePopup('bg-opacity-50')
        else animatePopup('bg-opacity-0')
    }, [display_appointmentDrawer])

    const handleLocationEntitlements = () => {
        const location = entitlements.content.find(
            (item: { key: string }) => item.key === 'LOCATION_LIMIT'
        )

        if (location && location.currentValue === '0') {
            setShowLocationPopup(true)
        } else {
            NewLocationModal.open()
        }
    }

    const handleStaffEntitlements = () => {
        const staff = entitlements?.content?.find(
            (item: { key: string }) => item.key === 'STAFF_LIMIT'
        )
        if (staff) {
            if (staff?.currentValue !== '0') {
                NewStaffModal.open()
            } else {
                setShowStaffPopup(true)
            }
        }
    }


    function handleUniversalButtonChange(value: string) {
        switch (value) {
            case UNIVERSAL_CREATE.CLIENT:
                NewClientModal.setAttributes({ is_new: true })
                NewClientModal.open()
                break
            case UNIVERSAL_CREATE.STAFF:
                handleStaffEntitlements()
                break
            case UNIVERSAL_CREATE.SERVICE:
                NewServiceModal.open()
                break
            case UNIVERSAL_CREATE.MEMBERSHIP:
                NewMembershipModal.open()
                break
            case UNIVERSAL_CREATE.LOCATION:
                handleLocationEntitlements()
                break
            case UNIVERSAL_CREATE.CLASS:
                NewClassModal.open()
                break
            case UNIVERSAL_CREATE.APPOINTMENT:
                setDisplayAppointmentDrawer(true)
                break
            case UNIVERSAL_CREATE.SALE:
                authPOSModal.open()
            default:
                break
        }
    }
    
    useEffect(() => {
        if (!user?.uuid && !Cookies.get('id_token')) {
            LoginModalContext.open()
        }
    }, [user?.uuid])

    // useEffect(() => {
    //     if (!translations.HEADER_TRANSLATION_ID) {
    //         loadComponentTranslations('HEADER_TRANSLATION_ID')
    //     }
    // }, [translations])

    function DisplayTitle() {
        let title = ''
        if (router.pathname.substr(1) === 'dashboard' && user?.first_name) {
            title = `Welcome, ${user.first_name}!`
        } else {
            title = router.pathname.split('/').pop() || 'dashboard'
        }
        return (
            <h1 className="text-xl font-medium text-primary-light w-4/12">
                <Translation
                    content_key={title}
                    render_as="span"
                    className="capitalize"
                />
            </h1>
        )
    }

    const DisplayMobileAppBar = useCallback(() => {
        const { NEXT_PUBLIC_STATIC_DOMAIN } =
            getConfig().publicRuntimeConfig
        return (
            <>
                <AppBar
                    className={`flex flex-row px-4 pt-[44px] pb-4 items-center h-[42px] box-content mobilenavabar`}
                    themeColor="inherit"
                >
                    {show_mobile_search_bar ? (
                        <>
                            <AppBarSection className="w-fit">
                                <UniversalSearch />
                                <button
                                    onClick={() =>
                                        toggleMobileSearchBar(false)
                                    }
                                    className="px-4"
                                >
                                    <i className="feather feather-x text-black text-xl" />
                                </button>
                            </AppBarSection>
                        </>
                    ) : (
                        <>
                            <AppBarSection>
                                <img
                                    className='mobilelogo'
                                    src={`https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/aot_logo.svg`}
                                />
                            </AppBarSection>
                            <div className="grow" />
                            {hasRole([RoleTypes.SUPER_ADMIN]) && (
                                <AppBarSection className="flex mr-5">
                                    <button
                                        className="h-10 w-10 items-center justify-center text-lg border border-gray-three rounded-lg"
                                        onClick={() => {
                                            // TODO: show tenant switcher mobile
                                        }}
                                    >
                                        <i className="feather-filter text-sec-blue-gray" />
                                    </button>
                                </AppBarSection>
                            )}
                            {/* <AppBarSection className="mr-5"> //TODO: uncomment out when ready
                                <div ref={add_new_anchor}>
                                    <UniversalCreateButton
                                        handleUniversalButtonChange={
                                            handleUniversalButtonChange
                                        }
                                    />
                                </div>
                            </AppBarSection> */}
                            <AppBarSection className="flex flex-row gap-x-4 items-center">
                                <div
                                    className="cursor-pointer"
                                    onClick={() =>
                                        toggleMobileSearchBar(true)
                                    }
                                >
                                    <span className="feather feather-search text-xl font-black"></span>
                                </div>
                                <div
                                    className="cursor-pointer"
                                    onClick={() =>
                                        toggleShowMobileSidebar(
                                            !show_mobile_sidebar
                                        )
                                    }
                                >
                                    <span className="feather feather-menu text-xl font-black"></span>
                                </div>
                            </AppBarSection>
                        </>
                    )}
                </AppBar>
                <AppBar
                    className="flex bg-white px-4 pt-5 pb-4 relative border-t border-gray-four"
                    themeColor="inherit"
                >
                    <AppBarSection>
                        <DisplayTitle />
                    </AppBarSection>
                </AppBar>
            </>
        )
    }, [show_mobile_search_bar, sidebar_items])

    const DisplayDesktopAppBar = useCallback(() => {
        if (scheduling === 'true') {
            return null
        }
        return (
            <AppBar
                className="flex items-stretched py-3 justify-between shadow-lg z-10 relative"
                themeColor="inherit"
                id="appbar"
            >
                <AppBarSection>
                    <a
                        href={`/dashboard/calendar?lang=${locale}`}
                        className={classNames(
                            // is_drawer_expanded
                            //     ? 'w-44 mr-14 bg-center'
                            //     : 'w-24 sm:w-32 bg-top-left',
                            'w-44 ml-5 bg-contain h-7 block app-logo-icon'
                        )}
                    ></a>
                </AppBarSection>

                <AppBarSection className="flex text-center justify-center topnavbar">
                    <div className="flex flex-grow justify-between gap-3">
                    {/* <DisplayTitle /> */}
                    <UniversalSearch className="flex" />
                    {/* <div className="hidden lg:inline-block w-2/12"></div> */}
                </div>
                    {hasRole([RoleTypes.SUPER_ADMIN]) && (
                        <>
                            <div className="flex mr-5">
                                <TenantSelector />
                            </div>
                            {/* <AppBarSpacer className="lg:hidden" /> */}
                        </>
                    )}
                    <div className="mr-5" ref={add_new_anchor}>
                        <UniversalCreateButton
                            handleUniversalButtonChange={
                                handleUniversalButtonChange
                            }
                        />
                    </div>
                    {sidebar_items.find(
                        (item) => item.translation_key === 'notifications'
                    )?.is_hidden == false && (
                        <a
                            className="text-sm flex justify-center items-center text-custom-dark-cyan-blue font-bold hover:bg-primary-light hover:text-white border border-custom-dark-cyan-blue rounded-full w-8 h-8 cursor-pointer mr-5"
                            target="_blank"
                            onClick={(e) => {
                                e.preventDefault()
                                router.push('/dashboard/notifications/')
                            }}
                        >
                            <NotificationIcon
                                style={{ color: '#173878' }}
                            />
                        </a>
                    )}
                    <div className="page-actions mr-5">
                        <UserDropdown locale={locale} />
                    </div>
                    {hasRole([RoleTypes.ADMIN]) && (
                        <div className="page-actions">
                            <div className="flex items-center">
                                <div className="h-5 border border-custom-dark-cyan-blue mr-5" />
                                <button
                                    className="text-white text-sm  brdr-radius-custom  btn-gap bg-custom-dark-cyan-blue"
                                    onClick={() => {
                                        router.push('/get_started')
                                    }}
                                >
                                    <Translation content_key="common.get_started" />
                                </button>
                                <div className="mx-5 h-5 border border-custom-dark-cyan-blue" />
                            </div>
                        </div>
                    )}

                    <LanguageSelector />
                </AppBarSection>
            </AppBar>
        )
    }, [locale, sidebar_items, scheduling])
    return (
        <>
            <Head>
                <title>
                    {translations.dashboard_page_title || 'Dashboard'}
                </title>
                <meta property="og:title" content="Dashboard" key="title" />
                <link rel="icon" href={tenant_logo_url} />
            </Head>

            <Authenticated>
                {is_mobile ? (
                    <DisplayMobileAppBar />
                ) : (
                    <DisplayDesktopAppBar />
                )}

                <AppBar
                    className={classNames(
                        'circular p-0 bg-sky-100 h-5 flex justify-center',
                        !is_free_tier ||
                            localStorage.getItem('banner_hidden') === 'true'
                            ? 'hidden'
                            : ''
                    )}
                >
                    <div>
                        {getTranslation(
                            'free_trial_aot_free_plan',
                            translations
                        )}
                        <span
                            className="text-blue-800 cursor-pointer"
                            onClick={() => {
                                window.open(
                                    `https://${window.location.host}/dashboard/profile/`,
                                    '_self'
                                )
                            }}
                        >
                            {getTranslation(
                                'free_trial_upgrade',
                                translations
                            )}
                        </span>
                        {getTranslation(
                            'free_trial_plan_to_access_features',
                            translations
                        )}
                        <span
                            className="text-blue-800 cursor-pointer"
                            onClick={() => {
                                localStorage.setItem(
                                    'banner_hidden',
                                    'true'
                                )
                            }}
                        >
                            {getTranslation(
                                'free_trial_hide_message',
                                translations
                            )}
                        </span>
                    </div>
                </AppBar>

                {Object.keys(translations).length == 0 || !user?.id ? (
                    <LostConnection />
                ) : (
                    <>
                        <div
                            className={classNames(
                                is_mobile
                                    ? 'flex w-full shrink  z-30 overflow-hidden'
                                    : 'flex flex-col',
                                'print:hidden'
                            )}
                        >
                            
                                {scheduling === 'true' ? <>{children}</> : (
                                    <Sidebar
                                        isMini={is_mobile ? false : undefined}
                                        isExpanded={is_mobile ? false : undefined}
                                    >
                                        {/* <div className='mt-5 ml-8'>{DisplayTitle()}</div> */}
                                        <>{children}</>
                                        {/* {ClientSidebar()} */}
                                        <ClientSidebar />
                                    </Sidebar>
                                )}

                        </div>
                        <MobileSidebar
                            show={show_mobile_sidebar}
                            toggleShow={toggleShowMobileSidebar}
                        />
                        <TenantModal />
                        {/* <LoginModal /> */}
                        <CreateClientModal />
                        <StaffModal />
                        <StaffConnectCalendarModal />
                        <LocationModal />
                        <UpsellModal />
                        {/* <SignupModal /> */}
                        <ClassModal />
                        <CalendarPrintModal />
                        {bookingPageModalCustom.is_open && (
                            <CustomBookingPageModal />
                        )}
                        {/*
                            Updated Point Of Sale Module from Pop-up to New Page. (Below code is no more needed)
                         */}
                        {/* {authPOSModal.is_open && (
                            <POSContextContainer>
                                <POSModal />
                            </POSContextContainer>
                        )} */}

                        <UpsellPopup
                            popup_ref={add_new_anchor.current}
                            show_popup={show_location_popup}
                            setShowPopup={setShowLocationPopup}
                            type="location"
                        />
                        <UpsellPopup
                            popup_ref={add_new_anchor.current}
                            show_popup={show_staff_popup}
                            setShowPopup={setShowStaffPopup}
                            type="staff"
                        />

                        <ServiceModal />
                        <GlobalSchedulingLimitsModal />
                        <MembershipModal />
                        <PaymentModal />
                        <CreditCardModal />

                        <AppointmentAddModal />
                        <MembershipAddModal />
                        <MembershipEditModal />
                        <OrderEditModal />
                        <PurchaseSummaryModal />
                        <CreateCouponModal />
                        <Drawer
                            className={classNames(
                                !!attributes.drawer_items
                                    ? 'right-0'
                                    : '-right-loose',
                                'absolute top-0'
                            )}
                            onOverlayClick={() => {
                                setTimeout(() => {
                                    setAttributes({
                                        ...attributes,
                                        drawer_items:
                                            undefined as unknown as boolean,
                                    })
                                }, 200)
                            }}
                            expanded={!!attributes.drawer_items}
                            position="end"
                            mode="overlay"
                            items={
                                attributes.drawer_items as ModalDataAttributes[]
                            }
                            width={487}
                        />
                        <GlobalDialog />

                        {display_appointmentDrawer && (
                            <Popup
                                show={display_appointmentDrawer}
                                popupAlign={{
                                    horizontal: 'right',
                                    vertical: 'top',
                                }}
                                animate={false}
                                popupClass={classNames(
                                    'bg-black h-full  flex items-end',
                                    popup_anim_css
                                )}
                                style={{
                                    width: '100%',
                                    overflow: 'auto',
                                }}
                                className="z-[102]"
                            >
                                <div className="bg-white w-road h-full">
                                    <AppointmentDrawerContainer>
                                        <div className="">
                                            <AppointmentDrawer
                                                onClose={() => {
                                                    setDisplayAppointmentDrawer(
                                                        false
                                                    )
                                                }}
                                            />
                                        </div>
                                    </AppointmentDrawerContainer>
                                </div>
                            </Popup>
                        )}
                    </>
                )}
            </Authenticated>

            <NotAuthenticated>
                <span>Relogging you in</span>
                {/* <LoginModal /> */}
            </NotAuthenticated>
        </>
    )
}

export default Dashboard
